/**
 * Documento acadêmico: o que é, quais peças pode ter e como ele sai no papel.
 *
 * Este módulo é o **outro produto** do gerador. O miolo de agenda é desenhado em
 * milímetros, com altura de pauta calculada e quadros fixos; o documento
 * acadêmico é **texto que corre**: parágrafos, seções numeradas, figuras e
 * referências, paginados pela NBR 14724. São problemas diferentes, e por isso
 * têm módulos diferentes — o que eles compartilham é o papel A4, a impressão pelo
 * navegador e o PDF do servidor.
 *
 * Três ideias sustentam tudo aqui:
 *
 * 1. **As medidas são da norma, e moram no motor** (`abnt.ts`). Nada de número
 *    solto no desenho: se a norma muda, muda em um lugar e os testes acompanham.
 * 2. **O documento é dado, não desenho.** Aqui está o que a pessoa preencheu; o
 *    desenho (HTML/CSS) fica no site, porque é o navegador que pagina o texto.
 * 3. **Os itens são uma lista selecionável.** O modelo traz uma sugestão de quais
 *    peças entram (capa, folha de rosto, resumo, sumário, listas, referências...)
 *    e quem escreve marca e desmarca à vontade.
 */
import { folhaPadrao, formatoDoCapituloPadrao } from './folha.js';
/* ------------------------------------------------------------------ */
/* Modelos                                                             */
/* ------------------------------------------------------------------ */
/**
 * Os dois formatos que o editor oferece.
 *
 * - **Trabalho acadêmico** (NBR 14724): capa, folha de rosto, resumo, sumário,
 *   seções e referências. É o TCC, a monografia, a dissertação, a tese e o
 *   relatório de estágio.
 * - **Artigo científico** (NBR 6022): título, autoria, resumo e abstract,
 *   seções corridas e referências — sem capa nem folha de rosto, porque artigo
 *   não se encaderna: ele é publicado.
 */
export const MODELOS_DE_DOCUMENTO = ['TRABALHO_ACADEMICO', 'ARTIGO_CIENTIFICO', 'LIVRO'];
export const ROTULO_MODELO_DE_DOCUMENTO = {
    TRABALHO_ACADEMICO: 'Trabalho acadêmico (NBR 14724)',
    ARTIGO_CIENTIFICO: 'Artigo científico (NBR 6022)',
    LIVRO: 'Livro (capítulos)',
};
export const DESCRICAO_MODELO_DE_DOCUMENTO = {
    TRABALHO_ACADEMICO: 'TCC, monografia, dissertação, tese e relatório: capa, folha de rosto, resumo, sumário, seções e referências — com os elementos pré e pós-textuais que você marcar.',
    ARTIGO_CIENTIFICO: 'Título, autoria, resumo com palavras-chave, abstract, seções corridas e referências. Feito para publicar, com o resumo em duas línguas.',
    LIVRO: 'Capítulos numerados com sumário, abertura e fecho — sem resumo, sem nota de trabalho e sem referencial obrigatório. Para romance, contos, memórias, apostila ou livro de estudo. Pode sair em A4 ou em A5.',
};
/**
 * Os papéis em que um documento pode ser impresso.
 *
 * O trabalho acadêmico e o artigo são **A4** por norma (a NBR 14724 fixa o papel);
 * o livro escolhe — A5 é o tamanho de livro de bolso e de apostila, e A4 é o que
 * a pessoa tem na impressora de casa.
 */
export const PAPEIS_DE_DOCUMENTO = ['A4', 'A5'];
export const ROTULO_PAPEL_DE_DOCUMENTO = {
    A4: 'A4 (210 × 297 mm)',
    A5: 'A5 (148 × 210 mm)',
};
export const DESCRICAO_PAPEL_DE_DOCUMENTO = {
    A4: 'A folha da impressora comum e o papel exigido pela norma nos trabalhos acadêmicos.',
    A5: 'Metade do A4: o tamanho de livro, de apostila e de agenda. A margem e o corpo do texto encolhem junto, para a linha não ficar comprida demais.',
};
/** Papel de cada modelo (o acadêmico não escolhe: a norma fixa o A4). */
export const PAPEL_PADRAO_DO_MODELO = {
    TRABALHO_ACADEMICO: 'A4',
    ARTIGO_CIENTIFICO: 'A4',
    LIVRO: 'A4',
};
/** Este modelo deixa escolher o papel? */
export function papelEscolhivel(modelo) {
    return modelo === 'LIVRO';
}
/**
 * Onde a legenda (o título) da figura é impressa.
 *
 * A NBR 14724 pede **acima** da imagem; o manual do Centro Paula Souza pede
 * **abaixo**. O número (“Figura 3”) anda junto com a legenda, seja qual for o
 * lado.
 */
export const POSICOES_DA_LEGENDA = ['ACIMA', 'ABAIXO'];
export const ROTULO_POSICAO_DA_LEGENDA = {
    ACIMA: 'Acima da figura (NBR 14724)',
    ABAIXO: 'Abaixo da figura (manual do CPS)',
};
/** A posição da legenda (ausente = acima, como todo documento já salvo imprime). */
export function posicaoDaLegenda(documento) {
    return documento.legendaDaFigura === 'ABAIXO' ? 'ABAIXO' : 'ACIMA';
}
/* ------------------------------------------------------------------ */
/* Itens do documento                                                  */
/* ------------------------------------------------------------------ */
/** Os grupos na ordem em que aparecem no papel. */
export const GRUPOS_DOCUMENTO = ['PRE_TEXTUAL', 'TEXTUAL', 'POS_TEXTUAL'];
export const ROTULO_GRUPO_DOCUMENTO = {
    PRE_TEXTUAL: 'Pré-textuais',
    TEXTUAL: 'Textuais',
    POS_TEXTUAL: 'Pós-textuais',
};
export const DESCRICAO_GRUPO_DOCUMENTO = {
    PRE_TEXTUAL: 'Vêm antes do texto e não são numerados nem entram no sumário (a paginação conta, mas não aparece neles).',
    TEXTUAL: 'O corpo do trabalho: introdução, desenvolvimento e conclusão ou considerações finais.',
    POS_TEXTUAL: 'Vêm depois do texto, em folha própria.',
};
/**
 * Todas as peças que um documento pode ter.
 *
 * A ordem da lista é a ordem no papel — é ela que o motor segue, e por isso ela
 * não é alfabética: capa, folha de rosto, aprovação, dedicatória, agradecimentos,
 * epígrafe, resumo, abstract, listas, sumário, texto, referências, apêndices.
 */
export const ELEMENTOS_DOCUMENTO = [
    'CAPA',
    'FOLHA_DE_ROSTO',
    'FOLHA_DE_APROVACAO',
    'DEDICATORIA',
    'AGRADECIMENTOS',
    'EPIGRAFE',
    'RESUMO',
    'ABSTRACT',
    'LISTA_DE_FIGURAS',
    'LISTA_DE_TABELAS',
    'LISTA_DE_ABREVIATURAS',
    'SUMARIO',
    'RESUMO_DO_ARTIGO',
    /** Apresentação ou prefácio: o texto de quem escreve antes do primeiro capítulo. */
    'APRESENTACAO',
    /** Os capítulos do livro (e as seções do trabalho). */
    'CAPITULOS',
    'REFERENCIAL_TEORICO',
    'METODOLOGIA',
    'RESULTADOS',
    'CONSIDERACOES_FINAIS',
    /** O fecho do livro: epílogo, posfácio, últimas palavras. */
    'EPILOGO',
    /** Página de fecho, com quem escreveu. */
    'SOBRE_O_AUTOR',
    'REFERENCIAS',
    'GLOSSARIO',
    'APENDICES',
    'ANEXOS',
    /** Folhas pautadas no fim, para anotar à mão depois de impresso. */
    'PAGINA_DE_ANOTACAO',
];
export const ROTULO_ELEMENTO = {
    CAPA: 'Capa',
    FOLHA_DE_ROSTO: 'Folha de rosto',
    FOLHA_DE_APROVACAO: 'Folha de aprovação',
    DEDICATORIA: 'Dedicatória',
    AGRADECIMENTOS: 'Agradecimentos',
    EPIGRAFE: 'Epígrafe',
    RESUMO: 'Resumo',
    ABSTRACT: 'Abstract',
    LISTA_DE_FIGURAS: 'Lista de figuras',
    LISTA_DE_TABELAS: 'Lista de tabelas',
    LISTA_DE_ABREVIATURAS: 'Lista de abreviaturas e siglas',
    SUMARIO: 'Sumário',
    RESUMO_DO_ARTIGO: 'Resumo e abstract do artigo',
    APRESENTACAO: 'Apresentação',
    CAPITULOS: 'Capítulos',
    REFERENCIAL_TEORICO: 'Referencial teórico',
    METODOLOGIA: 'Metodologia',
    RESULTADOS: 'Resultados e discussão',
    CONSIDERACOES_FINAIS: 'Considerações finais',
    EPILOGO: 'Epílogo',
    SOBRE_O_AUTOR: 'Sobre o autor',
    REFERENCIAS: 'Referências',
    GLOSSARIO: 'Glossário',
    APENDICES: 'Apêndices',
    ANEXOS: 'Anexos',
    PAGINA_DE_ANOTACAO: 'Páginas de anotação',
};
export const DESCRICAO_ELEMENTO = {
    CAPA: 'Instituição, autor, título, cidade e ano — a primeira folha, sem numeração aparente.',
    FOLHA_DE_ROSTO: 'Autor, título e a nota que diz o que é o trabalho, com o nome do orientador.',
    FOLHA_DE_APROVACAO: 'Para colher as assinaturas da banca, com o nome de cada examinador.',
    DEDICATORIA: 'Uma ou duas linhas, à direita e no pé da folha.',
    AGRADECIMENTOS: 'Texto curto, em folha própria.',
    EPIGRAFE: 'Uma citação que abre o trabalho, com a autoria.',
    RESUMO: 'Um parágrafo, espaço simples, com as palavras-chave abaixo.',
    ABSTRACT: 'A mesma coisa em outra língua — para quem vai ler fora do país.',
    LISTA_DE_FIGURAS: 'Figura, legenda e página — montada a partir das figuras que você inserir.',
    LISTA_DE_TABELAS: 'Tabela, título e página — montada a partir das tabelas que você inserir.',
    LISTA_DE_ABREVIATURAS: 'Cada sigla com o seu significado, em ordem alfabética.',
    SUMARIO: 'Os títulos das seções com a numeração e a página, alinhados à direita.',
    RESUMO_DO_ARTIGO: 'Resumo e abstract lado a lado, com as palavras-chave de cada idioma.',
    APRESENTACAO: 'O texto de quem escreve antes do primeiro capítulo, em folha própria.',
    CAPITULOS: 'Os capítulos do livro (no trabalho acadêmico, são as seções com o seu texto).',
    REFERENCIAL_TEORICO: 'O que já se escreveu sobre o assunto, em seções suas.',
    METODOLOGIA: 'Como o trabalho foi feito: tipo de pesquisa, materiais e passos.',
    RESULTADOS: 'O que apareceu, com tabelas, figuras e a discussão.',
    CONSIDERACOES_FINAIS: 'O que ficou e o que ainda falta investigar.',
    EPILOGO: 'O fecho do livro, em folha própria.',
    SOBRE_O_AUTOR: 'Uma página no fim, com quem escreveu — o que a contracapa costuma trazer.',
    REFERENCIAS: 'As obras citadas, em ordem alfabética e na NBR 6023.',
    GLOSSARIO: 'As palavras técnicas do trabalho, com o significado.',
    APENDICES: 'Material que você mesmo produziu (questionário, roteiro, código).',
    ANEXOS: 'Material de terceiros que embasa o trabalho (documento, norma, laudo).',
    PAGINA_DE_ANOTACAO: 'Folhas pautadas ou de bolinhas no fim do documento, para escrever à mão. Você escolhe quantas e como são pautadas — ou folha lisa, para desenhar.',
};
/**
 * O grupo de cada peça, na ordem do papel.
 *
 * O editor agrupa os cartões por ele e a impressão usa o grupo para saber o que
 * sai **sem faixa** — as peças pré-textuais abrem o documento e não levam
 * cabeçalho nem número de página (a contagem corre, mas o número só aparece a
 * partir do texto, como manda a norma e como faz o livro impresso).
 */
export const GRUPO_DE_ELEMENTO_DOCUMENTO = {
    CAPA: 'PRE_TEXTUAL',
    FOLHA_DE_ROSTO: 'PRE_TEXTUAL',
    FOLHA_DE_APROVACAO: 'PRE_TEXTUAL',
    DEDICATORIA: 'PRE_TEXTUAL',
    AGRADECIMENTOS: 'PRE_TEXTUAL',
    EPIGRAFE: 'PRE_TEXTUAL',
    RESUMO: 'PRE_TEXTUAL',
    ABSTRACT: 'PRE_TEXTUAL',
    LISTA_DE_FIGURAS: 'PRE_TEXTUAL',
    LISTA_DE_TABELAS: 'PRE_TEXTUAL',
    LISTA_DE_ABREVIATURAS: 'PRE_TEXTUAL',
    SUMARIO: 'PRE_TEXTUAL',
    RESUMO_DO_ARTIGO: 'PRE_TEXTUAL',
    APRESENTACAO: 'PRE_TEXTUAL',
    CAPITULOS: 'TEXTUAL',
    REFERENCIAL_TEORICO: 'TEXTUAL',
    METODOLOGIA: 'TEXTUAL',
    RESULTADOS: 'TEXTUAL',
    CONSIDERACOES_FINAIS: 'TEXTUAL',
    EPILOGO: 'POS_TEXTUAL',
    SOBRE_O_AUTOR: 'POS_TEXTUAL',
    REFERENCIAS: 'POS_TEXTUAL',
    GLOSSARIO: 'POS_TEXTUAL',
    APENDICES: 'POS_TEXTUAL',
    ANEXOS: 'POS_TEXTUAL',
    PAGINA_DE_ANOTACAO: 'POS_TEXTUAL',
};
/** Em que modelos cada peça faz sentido. */
export const ELEMENTOS_POR_MODELO = {
    TRABALHO_ACADEMICO: [
        'CAPA',
        'FOLHA_DE_ROSTO',
        'FOLHA_DE_APROVACAO',
        'DEDICATORIA',
        'AGRADECIMENTOS',
        'EPIGRAFE',
        'RESUMO',
        'ABSTRACT',
        'LISTA_DE_FIGURAS',
        'LISTA_DE_TABELAS',
        'LISTA_DE_ABREVIATURAS',
        'SUMARIO',
        'REFERENCIAL_TEORICO',
        'METODOLOGIA',
        'RESULTADOS',
        'CONSIDERACOES_FINAIS',
        'REFERENCIAS',
        'GLOSSARIO',
        'APENDICES',
        'ANEXOS',
        'PAGINA_DE_ANOTACAO',
    ],
    ARTIGO_CIENTIFICO: [
        'RESUMO_DO_ARTIGO',
        'REFERENCIAL_TEORICO',
        'METODOLOGIA',
        'RESULTADOS',
        'CONSIDERACOES_FINAIS',
        'REFERENCIAS',
        'GLOSSARIO',
        'APENDICES',
        'ANEXOS',
        'PAGINA_DE_ANOTACAO',
    ],
    /*
     * O livro não tem nada de acadêmico por obrigação: sem resumo, sem folha de
     * aprovação, sem referencial teórico e — por padrão — sem referências. O que
     * ele tem é capa, sumário e capítulos, com as folhas de abertura e de fecho
     * que a pessoa quiser.
     */
    LIVRO: [
        'CAPA',
        'FOLHA_DE_ROSTO',
        'DEDICATORIA',
        'EPIGRAFE',
        'APRESENTACAO',
        'SUMARIO',
        'CAPITULOS',
        'LISTA_DE_FIGURAS',
        'EPILOGO',
        'SOBRE_O_AUTOR',
        'REFERENCIAS',
        'GLOSSARIO',
        'APENDICES',
        'ANEXOS',
        'PAGINA_DE_ANOTACAO',
    ],
};
/** O que vem marcado quando o documento nasce de cada modelo. */
export const ITENS_PADRAO_DO_MODELO = {
    TRABALHO_ACADEMICO: [
        'CAPA',
        'FOLHA_DE_ROSTO',
        'RESUMO',
        'SUMARIO',
        'REFERENCIAL_TEORICO',
        'METODOLOGIA',
        'RESULTADOS',
        'CONSIDERACOES_FINAIS',
        'REFERENCIAS',
    ],
    ARTIGO_CIENTIFICO: [
        'RESUMO_DO_ARTIGO',
        'REFERENCIAL_TEORICO',
        'METODOLOGIA',
        'RESULTADOS',
        'CONSIDERACOES_FINAIS',
        'REFERENCIAS',
    ],
    /* Um livro começa com capa, sumário e capítulos — o resto é escolha. */
    LIVRO: ['CAPA', 'SUMARIO', 'CAPITULOS'],
};
/**
 * Peças que **não podem ser desmarcadas**.
 *
 * A norma exige: no trabalho acadêmico, capa, folha de rosto, referências e o
 * texto; no artigo, o resumo, as referências e o texto. O resto é escolha de quem
 * escreve — listas de figuras só fazem sentido se houver figuras, por exemplo.
 */
export const ELEMENTOS_OBRIGATORIOS = {
    TRABALHO_ACADEMICO: ['CAPA', 'FOLHA_DE_ROSTO', 'REFERENCIAS'],
    ARTIGO_CIENTIFICO: ['RESUMO_DO_ARTIGO', 'REFERENCIAS'],
    /*
     * Livro não tem peça obrigatória: quem escreve decide o que o livro dele tem.
     * O padrão já traz capa, sumário e capítulos, e daí para frente é escolha —
     * um livro de poemas pode não ter capítulo nenhum.
     */
    LIVRO: [],
};
/** O item é obrigatório neste modelo? (o assistente usa para travar o cartão) */
export function elementoObrigatorio(modelo, item) {
    return ELEMENTOS_OBRIGATORIOS[modelo].includes(item);
}
/**
 * As peças que abrem **folha nova**.
 *
 * A norma manda começar em nova folha os elementos pré-textuais, o texto, as
 * referências e cada item pós-textual. Sem esta lista o sumário sairia colado no
 * resumo e a folha de aprovação nasceria no meio de uma página.
 */
export const ELEMENTOS_EM_FOLHA_NOVA = [
    'CAPA',
    'FOLHA_DE_ROSTO',
    'FOLHA_DE_APROVACAO',
    'DEDICATORIA',
    'AGRADECIMENTOS',
    'EPIGRAFE',
    'RESUMO',
    'ABSTRACT',
    'LISTA_DE_FIGURAS',
    'LISTA_DE_TABELAS',
    'LISTA_DE_ABREVIATURAS',
    'SUMARIO',
    'RESUMO_DO_ARTIGO',
    'APRESENTACAO',
    'EPILOGO',
    'SOBRE_O_AUTOR',
    'REFERENCIAS',
    'GLOSSARIO',
    'APENDICES',
    'ANEXOS',
];
/**
 * As peças que ocupam a **folha inteira** e não correm com o texto.
 *
 * A folha de anotação é uma folha pautada: ela não cresce nem encolhe, e a
 * seguinte é outra folha igual. Quem desenha precisa saber disso para dar a ela a
 * altura exata da **página de anotação** — que é o que o motor calcula
 * (`pautaDaAnotacao`): a folha inteira com a margem estreita da anotação, e não a
 * mancha do texto.
 */
export const ELEMENTOS_DE_FOLHA_INTEIRA = ['PAGINA_DE_ANOTACAO'];
/** Tipo de trabalho, na nota da folha de rosto. */
export const TIPOS_DE_TRABALHO = [
    'TCC',
    'MONOGRAFIA',
    'DISSERTACAO',
    'TESE',
    'RELATORIO',
    'PROJETO',
];
export const ROTULO_TIPO_DE_TRABALHO = {
    TCC: 'Trabalho de conclusão de curso',
    MONOGRAFIA: 'Monografia',
    DISSERTACAO: 'Dissertação de mestrado',
    TESE: 'Tese de doutorado',
    RELATORIO: 'Relatório',
    PROJETO: 'Projeto de pesquisa',
};
/**
 * Os tipos de bloco do texto.
 *
 * O documento era uma lista de parágrafos com **uma** citação longa no fim; virou
 * uma lista de blocos porque é assim que quem escreve pensa — e porque cada tipo
 * tem o seu formato na norma, o seu espaço antes e depois, e o seu jeito de não
 * partir entre duas folhas.
 *
 * O `TITULO` não está aqui de propósito: título é a **seção** (que numera, entra no
 * sumário e abre folha), e não um pedaço do texto.
 */
export const TIPOS_DE_BLOCO = ['PARAGRAFO', 'CITACAO', 'LISTA', 'TABELA', 'IMAGEM', 'NOTA'];
export const ROTULO_TIPO_DE_BLOCO = {
    PARAGRAFO: 'Parágrafo',
    CITACAO: 'Citação longa',
    LISTA: 'Lista',
    TABELA: 'Tabela',
    IMAGEM: 'Imagem',
    NOTA: 'Nota',
};
export const DESCRICAO_TIPO_DE_BLOCO = {
    PARAGRAFO: 'O texto corrido, com o recuo de primeira linha que o documento escolher.',
    CITACAO: 'Mais de três linhas: recuo de 4 cm, corpo menor e espaço simples, sem aspas (NBR 10520).',
    LISTA: 'Enumeração, com marcador ou número. A norma pede o mesmo recuo do parágrafo.',
    TABELA: 'Dados em linhas e colunas, com legenda em cima e fonte embaixo.',
    IMAGEM: 'Figura no meio do texto, com legenda em cima e fonte embaixo (NBR 14724).',
    NOTA: 'No texto sai só o número; a nota sai no pé da folha, com a lista ao fim da seção ou do documento.',
};
/** Como a lista é marcada. */
export const FORMATOS_DE_LISTA = ['MARCADA', 'NUMERADA'];
export const ROTULO_FORMATO_DE_LISTA = {
    MARCADA: 'Com marcadores',
    NUMERADA: 'Numerada',
};
/**
 * O nível da seção vai de **1 a 6**.
 *
 * A numeração progressiva da NBR 6024 desce até a seção **quinária** (1.1.1.1.1), e quem
 * escreve com muitos desdobramentos usa mais: o editor oferece **Título 1 a Título 6** e o
 * motor numera o que vier, sem teto escondido.
 */
export const NIVEL_MAXIMO_DE_SECAO = 6;
/** A tag do editor que representa o nível: `h1` é o nível 1 (e o teto é o 6). */
export function tagDoNivel(nivel) {
    const dentro = Number.isFinite(nivel) ? Math.round(nivel) : 1;
    return `h${Math.min(NIVEL_MAXIMO_DE_SECAO, Math.max(1, dentro))}`;
}
/** O nível que a tag do editor representa (`h3` é o nível 3; o que não for tag vira 1). */
export function nivelDaTag(tag) {
    const numero = Number.parseInt(tag.replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(numero) || numero < 1)
        return 1;
    return Math.min(NIVEL_MAXIMO_DE_SECAO, numero);
}
/** A tag é um título de seção? (`h1` a `h6`) */
export function ehTagDeTitulo(tag) {
    return /^h[1-6]$/i.test(tag);
}
/** O seletor de todos os títulos de seção — o mesmo no editor e no HTML. */
export const SELETOR_DE_TITULOS = 'h1, h2, h3, h4, h5, h6';
/**
 * Tipos de referência que o motor formata sozinho (NBR 6023, com a forma de cada norma).
 *
 * A ordem é a da lista que aparece no cartão: primeiro o que a banca pede mais (livro,
 * capítulo, artigo), depois os periódicos de leitura diária (jornal, revista), o evento, o
 * trabalho acadêmico, a norma legal, o site e os **suportes de mídia** (vídeo, áudio, filme,
 * imagem) — o texto livre fecha.
 */
export const TIPOS_DE_REFERENCIA = [
    'LIVRO',
    'CAPITULO',
    'ARTIGO',
    'JORNAL',
    'REVISTA',
    'EVENTO',
    'TRABALHO',
    'LEGISLACAO',
    'SITE',
    'VIDEO',
    'AUDIO',
    'FILME',
    'IMAGEM',
    'LIVRE',
];
/**
 * As normas que o gerador escreve, além do papel.
 *
 * A ABNT é o padrão (documento sem `norma` é ABNT). As numeradas — IEEE e Vancouver —
 * ainda não entram: nelas o número da lista vem da **ordem da primeira citação**, e o
 * texto guarda a citação como ela foi escrita, sem registro de ordem. Ver
 * `documento/normas.ts` para a razão completa.
 */
export const NORMAS = ['ABNT', 'APA', 'IEEE', 'VANCOUVER'];
/**
 * As normas **numeradas**: nelas a lista sai numerada e a citação no texto é o número
 * (`[1]` no IEEE, `(1)` no Vancouver), e não o sobrenome com o ano.
 *
 * O número vem da **ordem da primeira citação** (é a regra das duas), e por isso ele mora
 * na referência (`numero`) em vez de ser contado na hora: quem cita primeiro recebe o
 * número primeiro, e a lista sai na ordem em que as obras aparecem no texto.
 */
export const NORMAS_NUMERADAS = ['IEEE', 'VANCOUVER'];
export function normaNumerada(norma) {
    return NORMAS_NUMERADAS.includes(norma);
}
export const ROTULO_NORMA = {
    ABNT: 'ABNT (NBR 6023 e NBR 10520)',
    APA: 'APA (7ª edição)',
    IEEE: 'IEEE (lista numerada)',
    VANCOUVER: 'Vancouver (lista numerada)',
};
export const DESCRICAO_NORMA = {
    ABNT: 'Sobrenome em caixa alta, título em negrito e cidade na referência; a citação sai como (SOUZA, 2020).',
    APA: 'Sobrenome em caixa mista, ano logo depois dos autores, título em itálico e sem cidade; a citação sai como (Souza, 2020).',
    IEEE: 'Lista numerada na ordem em que as obras são citadas, autores com as iniciais na frente e título do artigo entre aspas; a citação sai como [1].',
    VANCOUVER: 'Lista numerada na ordem em que as obras são citadas, sobrenome com as iniciais coladas (Silva J) e revista com ano;volume(número):páginas; a citação sai como (1).',
};
export const ROTULO_TIPO_DE_REFERENCIA = {
    LIVRO: 'Livro',
    CAPITULO: 'Capítulo de livro',
    ARTIGO: 'Artigo de periódico',
    JORNAL: 'Reportagem de jornal',
    REVISTA: 'Reportagem de revista',
    EVENTO: 'Trabalho em evento (anais de congresso)',
    TRABALHO: 'Trabalho acadêmico (TCC, dissertação, tese)',
    LEGISLACAO: 'Legislação ou norma técnica',
    SITE: 'Página da internet',
    VIDEO: 'Vídeo (YouTube, videoaula)',
    AUDIO: 'Áudio (podcast, rádio, música)',
    FILME: 'Filme ou documentário',
    IMAGEM: 'Imagem, fotografia ou obra de arte',
    LIVRE: 'Como eu escrevi (sem reformatar)',
};
/*
 * Os campos que aparecem em quase todo tipo. O `local` é o mais traiçoeiro: ele é a
 * **cidade** no livro e o **nome do jornal** na reportagem, e por isso o rótulo é sempre
 * reescrito no tipo em que ele muda de sentido.
 */
const AUTORES = {
    campo: 'autores',
    rotulo: 'Autores',
    dica: 'Um por linha, como “Maria Silva”.',
    linhas: 2,
};
const TITULO = { campo: 'titulo', rotulo: 'Título' };
const SUBTITULO = { campo: 'subtitulo', rotulo: 'Subtítulo' };
const ANO = { campo: 'ano', rotulo: 'Ano' };
const URL = { campo: 'url', rotulo: 'Endereço (URL)' };
const ACESSO = { campo: 'acessoEm', rotulo: 'Acesso em' };
const PAGINAS = { campo: 'paginas', rotulo: 'Páginas' };
/**
 * O cartão da referência, tipo por tipo: quais campos e com que nome.
 *
 * A mesma lista serve à tela e ao que a norma escreve — quem cita a reportagem de jornal
 * precisa do **caderno** e da **data do dia**, quem cita o podcast precisa do **programa** e
 * da **duração**, e é isso que cada lista diz.
 */
export const CAMPOS_DA_REFERENCIA = {
    LIVRO: [
        AUTORES,
        TITULO,
        SUBTITULO,
        { campo: 'edicao', rotulo: 'Edição', dica: 'Só o número: 3.' },
        { campo: 'local', rotulo: 'Cidade' },
        { campo: 'editora', rotulo: 'Editora' },
        ANO,
        URL,
        ACESSO,
    ],
    CAPITULO: [
        AUTORES,
        { ...TITULO, dica: 'O título do capítulo, não o do livro.' },
        {
            campo: 'local',
            rotulo: 'Livro (a obra)',
            dica: 'O título do livro em que o capítulo está.',
        },
        { campo: 'editora', rotulo: 'Editora' },
        { campo: 'edicao', rotulo: 'Edição' },
        PAGINAS,
        ANO,
        URL,
        ACESSO,
    ],
    ARTIGO: [
        AUTORES,
        TITULO,
        SUBTITULO,
        { campo: 'local', rotulo: 'Periódico', dica: 'O nome da revista científica.' },
        { campo: 'volume', rotulo: 'Volume' },
        { campo: 'numero', rotulo: 'Número', dica: 'O fascículo.' },
        PAGINAS,
        ANO,
        URL,
        ACESSO,
    ],
    JORNAL: [
        AUTORES,
        { ...TITULO, rotulo: 'Título da reportagem' },
        SUBTITULO,
        { campo: 'local', rotulo: 'Jornal e cidade', dica: 'Ex.: Folha de S.Paulo, São Paulo.' },
        { campo: 'secao', rotulo: 'Caderno ou seção', dica: 'Ex.: Caderno Cotidiano.' },
        PAGINAS,
        {
            campo: 'dataPublicacao',
            rotulo: 'Data da edição',
            dica: 'O dia em que saiu (sai como “12 out. 2026”).',
        },
        ANO,
        URL,
        ACESSO,
    ],
    REVISTA: [
        AUTORES,
        { ...TITULO, rotulo: 'Título da reportagem' },
        SUBTITULO,
        { campo: 'local', rotulo: 'Revista e cidade', dica: 'Ex.: Veja, São Paulo.' },
        { campo: 'edicao', rotulo: 'Edição', dica: 'Ex.: 2910.' },
        { campo: 'numero', rotulo: 'Número' },
        { campo: 'volume', rotulo: 'Volume' },
        PAGINAS,
        { campo: 'dataPublicacao', rotulo: 'Data da edição', dica: 'O mês ou o dia da edição.' },
        ANO,
        URL,
        ACESSO,
    ],
    EVENTO: [
        AUTORES,
        { ...TITULO, dica: 'O título do trabalho apresentado.' },
        SUBTITULO,
        { campo: 'local', rotulo: 'Nome do evento', dica: 'Ex.: Congresso Brasileiro de Educação.' },
        { campo: 'numero', rotulo: 'Edição do evento', dica: 'Só o número: 12.' },
        { campo: 'editora', rotulo: 'Editora ou organizadora' },
        PAGINAS,
        ANO,
        URL,
        ACESSO,
    ],
    TRABALHO: [AUTORES, TITULO, SUBTITULO, { campo: 'instituicao', rotulo: 'Instituição' }, { campo: 'local', rotulo: 'Cidade' }, ANO, URL, ACESSO],
    LEGISLACAO: [
        { ...AUTORES, rotulo: 'Órgão ou jurisdição', dica: 'Ex.: BRASIL, ou o nome da entidade da norma.' },
        { ...TITULO, rotulo: 'Título da norma', dica: 'Ex.: Lei nº 9.394, de 20 de dezembro de 1996.' },
        { campo: 'subtitulo', rotulo: 'Ementa', linhas: 2 },
        {
            campo: 'veiculo',
            rotulo: 'Publicação oficial',
            dica: 'Ex.: Diário Oficial da União. Sem ela, a linha sai como a de um livro (norma técnica).',
        },
        { campo: 'local', rotulo: 'Cidade' },
        { campo: 'secao', rotulo: 'Seção', dica: 'Ex.: seção 1.' },
        PAGINAS,
        { campo: 'editora', rotulo: 'Sigla do órgão ou editora' },
        { campo: 'dataPublicacao', rotulo: 'Publicada em' },
        ANO,
        URL,
        ACESSO,
    ],
    SITE: [AUTORES, TITULO, SUBTITULO, { campo: 'local', rotulo: 'Nome do site' }, ANO, URL, ACESSO],
    VIDEO: [
        { ...AUTORES, rotulo: 'Quem publicou', dica: 'O canal, a escola ou quem assina o vídeo.' },
        TITULO,
        SUBTITULO,
        { campo: 'editora', rotulo: 'Canal ou produtora' },
        { campo: 'local', rotulo: 'Cidade' },
        { campo: 'duracao', rotulo: 'Duração', dica: 'Ex.: 12 min.' },
        ANO,
        URL,
        ACESSO,
    ],
    AUDIO: [
        { ...AUTORES, rotulo: 'Apresentador, intérprete ou banda' },
        { ...TITULO, dica: 'O episódio, a faixa ou o programa.' },
        SUBTITULO,
        { campo: 'veiculo', rotulo: 'Programa, álbum ou emissora' },
        { campo: 'editora', rotulo: 'Gravadora ou canal' },
        { campo: 'local', rotulo: 'Cidade' },
        { campo: 'duracao', rotulo: 'Duração', dica: 'Ex.: 32 min.' },
        ANO,
        URL,
        ACESSO,
    ],
    FILME: [
        { ...AUTORES, rotulo: 'Direção' },
        TITULO,
        SUBTITULO,
        { campo: 'editora', rotulo: 'Produtora ou distribuidora' },
        { campo: 'local', rotulo: 'País ou cidade' },
        { campo: 'duracao', rotulo: 'Duração', dica: 'Ex.: 1 h 35 min.' },
        ANO,
        URL,
        ACESSO,
    ],
    IMAGEM: [
        { ...AUTORES, rotulo: 'Autor da imagem' },
        TITULO,
        SUBTITULO,
        { campo: 'local', rotulo: 'Onde ela está', dica: 'Cidade, acervo ou nome da coleção.' },
        { campo: 'editora', rotulo: 'Museu, coleção ou site' },
        ANO,
        URL,
        ACESSO,
    ],
    LIVRE: [
        {
            campo: 'texto',
            rotulo: 'A referência como você escreveu',
            dica: 'A lista já pronta, colada de outro trabalho: nada é reformatado.',
            linhas: 3,
        },
    ],
};
/**
 * Os dois jeitos de apresentar o parágrafo que a norma admite.
 *
 * A NBR 14724 deixa escolher: ou o parágrafo começa com **recuo** de 1,25 cm na
 * primeira linha (e aí não se põe linha em branco entre um e outro), ou ele começa
 * na margem e os parágrafos são separados por **uma linha em branco**. Fazer as duas
 * coisas ao mesmo tempo é o erro mais comum de quem escreve no Word.
 */
export const FORMATOS_DE_PARAGRAFO = ['RECUADO', 'SIMPLES'];
export const ROTULO_FORMATO_DE_PARAGRAFO = {
    RECUADO: 'Recuado (1,25 cm na primeira linha)',
    SIMPLES: 'Simples (na margem, com linha em branco)',
};
export const DESCRICAO_FORMATO_DE_PARAGRAFO = {
    RECUADO: 'A primeira linha começa 1,25 cm para dentro e não há linha em branco entre os parágrafos — é o mais comum.',
    SIMPLES: 'O parágrafo começa na margem e os parágrafos são separados por uma linha em branco.',
};
/**
 * As **fontes** que o documento pode usar.
 *
 * A norma não obriga uma fonte: pede a **mesma no trabalho inteiro**. O gerador oferece as
 * duas que a banca espera ver — a **Times New Roman**, que é a dos exemplos da NBR 14724 e o
 * padrão de todo documento já salvo, e a **Arial** —, e a escolha vale para a mancha, para as
 * faixas da folha e para o editor (é o que a pessoa lê na tela antes de imprimir).
 */
export const FONTES_DO_DOCUMENTO = ['TIMES', 'ARIAL'];
export const ROTULO_FONTE_DO_DOCUMENTO = {
    TIMES: 'Times New Roman',
    ARIAL: 'Arial',
};
export const DESCRICAO_FONTE_DO_DOCUMENTO = {
    TIMES: 'Com serifa — a fonte dos exemplos da norma, e o padrão do gerador.',
    ARIAL: 'Sem serifa e um pouco mais larga: o texto ocupa mais folha, e o trabalho fica algumas páginas maior. A norma aceita, desde que seja a mesma no documento inteiro.',
};
/** A fonte do documento (ausente = Times New Roman, como todo documento já salvo imprime). */
export function fonteDoDocumento(documento) {
    return documento.fonte === 'ARIAL' ? 'ARIAL' : 'TIMES';
}
/**
 * A largura com que uma figura sem medida escolhida é impressa: a mancha inteira.
 *
 * A NBR não manda a figura ocupar a largura toda; manda que ela caiba na mancha. Este é
 * o valor que o editor oferece como ponto de partida, e o que toda figura já gravada usa
 * (o campo `larguraMm` do bloco é opcional).
 */
export const LARGURA_DE_IMAGEM_MM = 140;
/**
 * Onde as **notas** saem.
 *
 * O navegador não sabe imprimir nota no pé da página (a `float: footnote` não existe no
 * Chromium): o mais perto do rodapé que se chega é o **fim da seção** — a nota sai logo
 * abaixo do texto a que pertence, separada por um filete, como a NBR a escreve. A outra
 * escolha é a nota de fim: todas juntas no fim do trabalho.
 */
export const POSICOES_DAS_NOTAS = ['FIM_DA_SECAO', 'FIM_DO_DOCUMENTO'];
export const ROTULO_POSICAO_DAS_NOTAS = {
    FIM_DA_SECAO: 'No fim da seção (a nota de rodapé)',
    FIM_DO_DOCUMENTO: 'No fim do documento (nota de fim)',
};
export const DESCRICAO_POSICAO_DAS_NOTAS = {
    FIM_DA_SECAO: 'A nota sai logo abaixo do texto da seção a que pertence, com um filete em cima — é o mais perto do pé da página que o navegador imprime.',
    FIM_DO_DOCUMENTO: 'Todas as notas em ordem, num bloco “Notas” no fim do trabalho, como a NBR escreve a nota de fim.',
};
/** Onde as notas saem (ausente = no fim da seção, que é o que todo documento salvo faz). */
export function posicaoDasNotas(documento) {
    return documento.notas === 'FIM_DO_DOCUMENTO' ? 'FIM_DO_DOCUMENTO' : 'FIM_DA_SECAO';
}
/* ------------------------------------------------------------------ */
/* Criação                                                             */
/* ------------------------------------------------------------------ */
/** Documento novo, vazio: o que o editor abre em branco. */
export function criarDocumentoPadrao(modelo = 'TRABALHO_ACADEMICO', parcial = {}) {
    return {
        modelo,
        papel: PAPEL_PADRAO_DO_MODELO[modelo],
        itens: [...ITENS_PADRAO_DO_MODELO[modelo]],
        identificacao: {
            titulo: '',
            subtitulo: '',
            autores: [{ nome: '' }],
            instituicao: '',
            unidade: '',
            curso: '',
            cidade: '',
            ano: String(new Date().getFullYear()),
            tipo: modelo === 'TRABALHO_ACADEMICO' ? 'TCC' : undefined,
            /* Livro e artigo não têm nota de folha de rosto. */
            orientador: '',
            banca: '',
        },
        resumo: '',
        palavrasChave: [],
        abstract: '',
        keywords: [],
        resumos: [],
        anexos: [],
        dedicatoria: '',
        agradecimentos: '',
        epigrafe: '',
        apresentacao: '',
        epilogo: '',
        sobreOAutor: '',
        abreviaturas: [],
        secoes: secoesDoModelo(modelo),
        figuras: [],
        referencias: [],
        capa: null,
        cabecalho: null,
        numerarPaginas: true,
        folha: folhaPadrao(modelo),
        capitulo: formatoDoCapituloPadrao(modelo),
        capitular: false,
        folhasDeAnotacao: 1,
        estiloDeAnotacao: 'LINHAS',
        ...parcial,
    };
}
/**
 * O esqueleto de seções que cada modelo entrega pronto.
 *
 * A pessoa renomeia, apaga e acrescenta — mas ninguém começa diante de uma folha
 * em branco: é a estrutura que a banca espera.
 */
export function secoesDoModelo(modelo) {
    const secao = (id, titulo, nivel, paragrafos = []) => ({
        id,
        titulo,
        nivel,
        paragrafos,
    });
    if (modelo === 'ARTIGO_CIENTIFICO') {
        return [
            secao('introducao', 'Introdução', 1),
            secao('referencial', 'Referencial teórico', 1),
            secao('metodologia', 'Metodologia', 1),
            secao('resultados', 'Resultados e discussão', 1),
            secao('conclusao', 'Considerações finais', 1),
        ];
    }
    /*
     * O livro nasce com três capítulos vazios.
     *
     * Parece pouco, e é de propósito: o capítulo é a unidade do livro, e quem
     * começa vendo "Capítulo 1" em branco entende na hora que é ali que o texto
     * entra — e que dá para renomear, apagar e acrescentar. Nada disso é impresso
     * enquanto não houver texto.
     */
    if (modelo === 'LIVRO') {
        return [secao('capitulo-1', 'Capítulo 1', 1), secao('capitulo-2', 'Capítulo 2', 1)];
    }
    return [
        secao('introducao', 'Introdução', 1),
        secao('referencial', 'Referencial teórico', 1),
        secao('metodologia', 'Metodologia', 1),
        secao('resultados', 'Resultados e discussão', 1),
        secao('conclusao', 'Considerações finais', 1),
    ];
}
/**
 * Os itens ajustados ao modelo, na ordem da norma.
 *
 * Sem esta função, trocar de modelo (trabalho ⇄ artigo) deixaria no documento
 * peças que não existem ali — uma capa em um artigo, um abstract em um relatório
 * sem língua estrangeira.
 */
/**
 * O papel do documento, com o padrão do modelo quando o campo está ausente.
 *
 * Miolo salvo antes deste recurso não tem `papel` — e era A4 (Regra 04).
 */
export function papelDoDocumento(documento) {
    if (!documento.papel)
        return PAPEL_PADRAO_DO_MODELO[documento.modelo];
    if (!papelEscolhivel(documento.modelo))
        return PAPEL_PADRAO_DO_MODELO[documento.modelo];
    return documento.papel;
}
export function itensDoModelo(modelo, itens) {
    const permitidos = new Set(ELEMENTOS_POR_MODELO[modelo]);
    const marcados = new Set(itens.filter((item) => permitidos.has(item)));
    for (const obrigatorio of ELEMENTOS_OBRIGATORIOS[modelo])
        marcados.add(obrigatorio);
    return ELEMENTOS_DOCUMENTO.filter((item) => marcados.has(item) && permitidos.has(item));
}
//# sourceMappingURL=tipos.js.map